import React, { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import { hideAll } from 'tippy.js';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ModalNext, ModalHeader, ModalBody, ModalFooter, CurrencyValue } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { GatewayEndorsementService } from 'gw-capability-gateway-policychange';
import { PolicyChange } from 'gw-capability-policychange-common-react'
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { MLQuoteNameUtil } from 'ml-portals-util-js';
import { MLModalService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLTagManagerService } from 'ml-portals-services-react'

import metadata from './MLBundleManager.metadata.json5';
import messages from './MLBundleManager.messages';
import styles from './MLBundleManager.module.scss';

import { Button, CheckboxField } from '@jutro/legacy/components';

import { IntlContext } from '@jutro/legacy/locale';

const BUNDLED = 1;
const NOT_BUNDLED = 0;

function MLBundleManager(props) {
    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const { authHeader } = useAuthentication();
    const ModalService = MLModalService();

    const {
        policies,
        isOpen,
        onReject,
    } = props;

    const filteredPolicies = useMemo(() => {
        return policies.filter((policy) => !policy.isCancelled);
    }, [policies]);

    const [processingBind, updateProcessingBind] = useState(false);
    const [bundleErrorMsg, updateBundleErrorMsg] = useState([]);
    const [anyPolicyHasPendingChange, ] = useState(filteredPolicies.some((policy) => policy.hasPendingChanges_ML));
    const [policyStatus, ] = useState(filteredPolicies.reduce((accumulator, policy) => ({...accumulator, [policy.policyNumber]: policy.bundleID_ML}), {}));
    const [newPolicyStatus, updateNewPolicyStatus] = useState(filteredPolicies.reduce((accumulator, policy) => ({...accumulator, [policy.policyNumber]: policy.bundleID_ML}), {}));

    useEffect(() => {
        // This functions hides all tooltip, used to hide the tooltip that opened this popup.
        hideAll();
    }, []);

    

    const _anyChangesInFlight = () => {
        return filteredPolicies.some((policy) => policy.changeInFlight);
    };

    const somePolicyHasChanges = () => {
        return filteredPolicies.some((policy) => policy.manageBundleChange);
    };

    const getPoliciesWithChanges = () => {
        return filteredPolicies.filter((policy) => policy.manageBundleChange);
    };

    const _allChangesAreQuoted = () => {
        return getPoliciesWithChanges().every((policy) => {
            return policy.manageBundleChange.status === 'Quoted';
        });
    };

    const _isBundled = (policy) => {
        return policy.bundleID_ML === BUNDLED;
    };

    const _getBundledPolicies = () => {
        return filteredPolicies.filter((policy) =>  _isBundled(policy));
    };

    /*
     * Bundle is valid if none are bundled (removing all from bundle)
     * or if at least two policies are in the bundle.
     */
    const isValidBundle = () => {
        return _getBundledPolicies().length !== 1;
    };

    const handleClose = () => {
        onReject();
        window.location.reload(false);
    };


    const _delay = (timeout) => {
        return new Promise((callback) => {
            setTimeout(() => {
                callback();
            }, timeout);
        });
    };
    
    const _waitForJobsToBeBound = () => {
        MLTagManagerService.sendFormStepEvent('Create Manage Bundle_Issue changes');
        updateProcessingBind(true);
        const jobIds = getPoliciesWithChanges().map((policy) => {
            return policy.manageBundleChange.jobID;
        });

        let retry = 20;
        ModalService.showProgressDialog(platformMessages.loading, undefined, false, 0);
        const waitForJobsToBeBound = () => {
            if (!retry--) {
                updateProcessingBind(false);
                handleClose();
                ModalService.hideProgressDialog(false);
                throw new Error('Too many retries');
            }

            GatewayEndorsementService.areJobsBound(jobIds, authHeader).then((response) => {
                if (response) {
                    updateProcessingBind(false);
                    handleClose();
                    ModalService.hideProgressDialog(false);
                    return;
                }
                _delay(5000).then(waitForJobsToBeBound);
            }, () => {
                console.error(new Error("Error checking binding status, retrying?"));
                _delay(5000).then(waitForJobsToBeBound);
            });
        };
        waitForJobsToBeBound();
    };
    
    const _wrappedEndorsementCall = (action, args, policy) => {
        policy.changeInFlight = true;
        return action(...args, authHeader).then((res) => {
            policy.manageBundleChange = new PolicyChange(res.policyChange || res);
            policy.changeInFlight = false;
        }, (error) => {
            console.error(`FAIL: ${action.name}`,
                'With args', ...args,
                `For policy: ${policy.policyNumber}`,
                'With error', error);
            policy.changeInFlight = false;
        });
    };

    const onContinue = () => {
        const bindPromises = getPoliciesWithChanges().map((policy) => {
            const change = policy.manageBundleChange;
            return _wrappedEndorsementCall(
                GatewayEndorsementService.bindEndorsement,
                [change.jobID, change.paymentDetails_ML],
                policy
            );
        });
        const promise = Promise.all(bindPromises);
        ModalService.showProgressDialog(platformMessages.loading, promise);
        promise.then(() => {
            _delay(200).then(_waitForJobsToBeBound)
        })
    };

    const onRequote = () => {
        const quotePromises = getPoliciesWithChanges().map((policy) => {
            return _wrappedEndorsementCall(
                GatewayEndorsementService.quoteEndorsement,
                [[policy.manageBundleChange.jobID]],
                policy
            )
        });
        ModalService.showProgressDialog(platformMessages.loading, Promise.all(quotePromises), false, 0);
    };

    const createBundleChanges = () => {
        updateBundleErrorMsg([]);
        const policyNumbers = [];
        const policyNumbersToChange = [];
        filteredPolicies.forEach((p)=> {
            if (newPolicyStatus[p.policyNumber] && policyStatus[p.policyNumber]) {
                policyNumbers.push(p.policyNumber);
                p.changeInFlight = true;
            }
            if (newPolicyStatus[p.policyNumber] !== policyStatus[p.policyNumber]) {
                policyNumbersToChange.push(p.policyNumber);
                p.changeInFlight = true;
            }
        });

        ModalService.showProgressDialog(platformMessages.loading,
            GatewayEndorsementService.createBundleChanges(policyNumbers, policyNumbersToChange, authHeader).then((res) => {
                res.forEach((pc) => {
                    filteredPolicies.forEach((p)=> {
                        if (p.policyNumber === pc.policyNumber) {
                            p.manageBundleChange = new PolicyChange(pc);
                            p.changeInFlight = false;
                            pc.bundleID_ML = p.bundleID_ML;
                        }
                    });
                });
                MLTagManagerService.sendFormStepEvent('Create Manage Bundle_Requote');
                _delay(200).then(onRequote);
            }).catch((error) => {
                const errorMessageStringArray = error.baseError.split(' ');
                const errorMessageIndex = errorMessageStringArray.lastIndexOf('ErrorMessage:');
                const errorMessageValue = errorMessageIndex !== -1 ? errorMessageStringArray[errorMessageIndex + 1] : undefined;
                let errorMessage;
                if(error.baseError.lastIndexOf('ErrorMessage:') > -1 && error.baseError.lastIndexOf('StackTrace_ML:') > -1){
                    errorMessage = error.baseError.slice(error.baseError.lastIndexOf('ErrorMessage:'), error.baseError.lastIndexOf('StackTrace_ML:'))
                        .replace("ErrorMessage:","")
                        .replace(errorMessageValue, "")
                        .replace("ERROR-803:", "")
                        .replace("ERROR-804:", "")
                        .trim();
                }
                if(errorMessage){
                    updateBundleErrorMsg([{message: errorMessage}]);
                }
                filteredPolicies.forEach((p)=> {
                    p.changeInFlight = false;
                });
            })
        );

    }

    const onCancel = () => {
        const jobs = [];
        filteredPolicies.forEach((p)=> {
            if (p.manageBundleChange && p.manageBundleChange.jobID) {
                jobs.push(p.manageBundleChange.jobID);
            }
        });

        ModalService.showProgressDialog(platformMessages.loading, GatewayEndorsementService.withdrawJobs(jobs, authHeader).then(() => {
            handleClose();
        }));

    }

    const shouldShowInvalidBundleMessage = () => {
        return !isValidBundle() && somePolicyHasChanges() && _allChangesAreQuoted();
    };

    const shouldShowPremiumColumn = () => {
        return !somePolicyHasChanges() || !_allChangesAreQuoted();
    };

    const is7DayAKPolicy = (policy) => {
        return policy.product.publicID === 'PersonalAuto' 
            && policy.termType_ML === '7Days' 
            && policy.address_ML.state === 'AK';
    };

    const getBundleModifiedSuccess = () => {
        return(
            <>
                <strong>{translator(messages.bundleModified)}</strong><br />
                {translator(messages.confirmChanges)}
            </>
        )
    }

    const getPolicyTypeDisplayName = (policy) => {
        return MLQuoteNameUtil.getProductName(policy.product.publicID);
    };

    const canChange = (effective, today) => {
        return (new Date(effective) < today);
    };

    const canContinue = () => {
        return (!somePolicyHasChanges() || _allChangesAreQuoted())
            && !_anyChangesInFlight()
            && isValidBundle()
            && !anyPolicyHasPendingChange;
    };

    const canCancel = () => {
        return !_anyChangesInFlight();
    };

    const canRequote = () => {
        let result = false;
        let numberOfPolciesInBundle = 0;
        filteredPolicies.forEach((p)=> {
            if (p.bundleID_ML) {
                numberOfPolciesInBundle++;
            }
            if (newPolicyStatus[p.policyNumber] !== policyStatus[p.policyNumber]) {
                result = true;
            }
        });
        if (numberOfPolciesInBundle === 1) {
            result = false;
        }
        return result;
    };

    const toggleBundleSelection = (policy) => {
        if (policy.bundleID_ML === BUNDLED) {
            policy.bundleID_ML = NOT_BUNDLED;
        } else {
            policy.bundleID_ML = BUNDLED;
        }
        const clonedNewPolicyStatus = {...newPolicyStatus};
        clonedNewPolicyStatus[policy.policyNumber] = policy.bundleID_ML;
        updateNewPolicyStatus(clonedNewPolicyStatus);
    };

    const getPreviousCost = (policy) => {
        return _.get(policy, 'manageBundleChange.previousTotalCost', null);
    };

    const getNewCost = (policy) => {
        return _.get(policy, 'manageBundleChange.totalCost', null);
    };

    const getChangeInCost = (policy) => {
        return _.get(policy, 'manageBundleChange.transactionCost', null);
    };

    const getPreviousTotal = () => {
        let total = 0;
        filteredPolicies.forEach((p) => {
            const val = getPreviousCost(p);
            if (val && val.amount) {
                total += val.amount;
            }
        });
        return {amount: total, currency: 'usd'};
    };

    const getNewCostTotal = () => {
        let total = 0;
        filteredPolicies.forEach((p) => {
            const val = getNewCost(p);
            if (val && val.amount) {
                total += val.amount;
            }
        });
        return {amount: total, currency: 'usd'};
    };

    const getChangeInCostTotal = (dialogScope) => {
        let total = 0;
        filteredPolicies.forEach((p) => {
            const val = getChangeInCost(dialogScope, p);
            if (val && val.amount) {
                total += val.amount;
            }
        });
        return {amount: total, currency: 'usd'};
    };
        
    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const wrapCell = (item, cell) => {
        if(is7DayAKPolicy(item)){
            return <span className={styles["shadow-row"]}>{cell}</span>
        }
        return cell;
    }

    const getTableField =  (item, index, property, wrap = true) => {
        const field = _.get(item, property.path);
        if(field && typeof field.value === 'object' ){
            if(field.value && field.value.name && field.value.hasOwnProperty('typelist')){
                return translator({id:field.value.name})
            }
            return wrap ? wrapCell(item, field.value) : field.value;
        }
        if(field && field._dtoName === 'Boolean'){
            if(field.value === true){
                const yes = translator({id:"ml.platform.inputs.Yes", defaultMessage: "Yes"});
                return wrap ? wrapCell(item, yes) : yes;
            }
            if(field.value === false){
                const no = translator({id:"ml.platform.inputs.No", defaultMessage: "No"});
                return wrap ? wrapCell(item, no) : no;
            }
            return undefined;
        }
        if(field && field.value){
            return wrap ? wrapCell(item, field.value) : field.value;
        }
        if(field){
            return wrap ? wrapCell(item, field) : field;
        }
        return undefined;
    }

    const dateCell =  (item, index, property) => {
        if(item.totalRow){
            return undefined;
        }
        const date = getTableField(item, index, property, false);
        if(date){
            return wrapCell(item, getFormattedDateString(date))
        }
        return undefined;
    }

    const todayCell = (item) => {
        if(item.totalRow){
            return undefined;
        }
        return getFormattedDateString(LocalDateUtil.today());
    }

    const policyTypeCell = (item) => {
        if(item.totalRow){
            return undefined;
        }
        return wrapCell(item, getPolicyTypeDisplayName(item));
    };

    const offeringCell = (item) => {
        if(item.totalRow){
            return undefined;
        }
        const offering = item.coveragePackageLevel_Ext && !item.isModernProduct_Ext ? item.coveragePackageLevel_Ext : item.offering_ML;
        return wrapCell(item, offering);
    };

    const bundledCell = (item, index) => {
        if(item.totalRow){
            return (<strong>{translator(messages.total)}</strong>);
        }
        if(!is7DayAKPolicy(item)){
            return (<CheckboxField 
                id={`bundledColumn${index}`} 
                disabled={!shouldShowPremiumColumn() || !canChange(item.effective, new Date())} 
                value={!!item.bundleID_ML}
                onValueChange={() => toggleBundleSelection(item)}
            />)
        }
        return wrapCell(item, translator(messages.notEligible));
    }

    const previousCell =  (item) => {
        const cost = item.totalRow ? getPreviousTotal() : getPreviousCost(item);
        const result = cost ? <CurrencyValue amount={cost.amount} currency={cost.currency} showFractions /> : " - ";
        return item.totalRow ? result : wrapCell(item, result);
    }

    const newCell =  (item) => {
        const cost = item.totalRow ? getNewCostTotal() : getNewCost(item);
        if(cost){
            const currencyComp = <strong><CurrencyValue amount={cost.amount} currency={cost.currency} showFractions /></strong>
            return item.totalRow ? (<>{currencyComp}</>) : wrapCell(item, currencyComp);
        }
        return wrapCell(item, " - ");
    }

    const proRataCell =  (item) => {
        const cost = item.totalRow ? getChangeInCostTotal() : getChangeInCost(item);
        const result = cost ? <CurrencyValue amount={cost.amount} currency={cost.currency} showFractions /> : " - ";
        return item.totalRow ? result : wrapCell(item, result);
    }

    const premiumCell =  (item) => {
        if(item.totalRow){
            return undefined;
        }
        const cost = item.premium;
        if(cost){
            return wrapCell(item, <CurrencyValue amount={cost.amount} currency={cost.currency} showFractions />)
        }
        return wrapCell(" - ");
    }

    const getMainTableData = () => {
        if(shouldShowPremiumColumn()){
            return filteredPolicies;
        }
        const policiesAndTotalRow = [...filteredPolicies];
        policiesAndTotalRow.push({ totalRow: true});
        return policiesAndTotalRow;
    }

    const overrides = {
        bundleErrors: {
            data: bundleErrorMsg,
            visible: bundleErrorMsg.length > 0
        },
        invalidBundleError: {
            visible: shouldShowInvalidBundleMessage()
        },
        anyPolicyHasPendingChangesError: {
            visible: anyPolicyHasPendingChange
        },
        bundleModifiedSuccess: {
            visible: !shouldShowPremiumColumn(),
            message: getBundleModifiedSuccess()
        },
        mainTable: {
            data: getMainTableData(),
            className: classNames("table", "table-striped", "overflow-table")
        },
        effectiveDateColumn: {
            visible: shouldShowPremiumColumn()
        },
        changeDateColumn: {
            visible: !shouldShowPremiumColumn()
        },
        previousColumn: {
            visible: !shouldShowPremiumColumn()
        },
        newColumn: {
            visible: !shouldShowPremiumColumn()
        },
        proRataColumn: {
            visible: !shouldShowPremiumColumn()
        },
        premiumColumn: {
            visible: shouldShowPremiumColumn()
        },
    };

    const callbackMap = {
        getTableField,
        dateCell,
        todayCell,
        policyTypeCell,
        offeringCell,
        bundledCell,
        previousCell,
        newCell,
        proRataCell,
        premiumCell
    };

    return (
        <ModalNext
            className={classNames(styles["ml-modal-dialog"], styles["dynamic-screen-width"])}
            overlayClassName=""
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            isOpen={isOpen}
            onRequestClose={handleClose}
        >
            {!processingBind &&
            <>
                <ModalHeader 
                    title={messages.title}
                    onClose={handleClose}
                    contentLayout={{
                        component: 'div',
                        componentProps: {
                            className: styles['modal-header']
                        }
                    }}
                    titleLayout={{
                        component: 'grid',
                        componentProps: {
                            className: styles['modal-title']
                        }
                    }}
                />
                <ModalBody
                    autoFocus
                    contentLayout={{
                        component: 'div',
                        componentProps: {
                            className: styles['modal-content']
                        }
                    }}
                >
                    <ViewModelForm
                        uiProps={metadata.componentContent}
                        overrideProps={overrides}
                        classNameMap={styles}
                        callbackMap={callbackMap}
                    />
                </ModalBody>
                <ModalFooter contentLayout={{
                    component: 'flex',
                    componentProps: {
                        className: styles["modal-footer"]
                    }
                }}
                >
                    {!shouldShowPremiumColumn() && 
                        <Button id="issueChangesBtn" disabled={!canContinue()} onClick={onContinue} type="filled">
                            {translator(messages.issueChanges)}
                        </Button>
                    }
                    {shouldShowPremiumColumn() && 
                        <Button id="requoteBtn" disabled={!canRequote()} onClick={createBundleChanges} type="filled">
                            {translator(messages.requote)}
                        </Button>
                    }
                    <Button id="cancelBtn" disabled={!canCancel()} onClick={onCancel} type="outlined">
                        {translator(messages.cancel)}
                    </Button>
                </ModalFooter>
            </>
            }
        </ModalNext>
    )
}


MLBundleManager.propTypes = {
    isOpen: PropTypes.bool,
    onReject: PropTypes.func,
    onResolve: PropTypes.func
};

MLBundleManager.defaultProps = {
    isOpen: false,
    onResolve: _.noop,
    onReject: _.noop
};

export default MLBundleManager;